// src/services/notifications.ts
import { supabase } from "../lib/supabase";
import type { ID } from "../types/domain";

export type TaskNotification = {
  id: ID;
  task_id: ID;
  user_id: ID;
  notification_type: 'assigned' | 'updated' | 'completed' | 'commented';
  message: string;
  read: boolean;
  created_at: string;
  task?: {
    id: ID;
    title: string;
    project_id: ID;
    project?: {
      id: ID;
      name: string;
      company_id: ID;
      company?: {
        id: ID;
        name: string;
      };
    };
  };
};

export async function getUserNotifications(userId: ID) {
  const { data, error } = await supabase
    .from('task_notifications')
    .select(`
      *,
      task:tasks(
        id,
        title,
        project_id,
        project:projects(
          id,
          name,
          company_id,
          company:companies(
            id,
            name
          )
        )
      )
    `)
    .eq('user_id', userId)
    .eq('read', false)
    .order('created_at', { ascending: false });

  return { data: data as TaskNotification[] | null, error };
}

export async function getUnreadNotificationCount(userId: ID) {
  const { count, error } = await supabase
    .from('task_notifications')
    .select('*', { count: 'exact', head: true })
    .eq('user_id', userId)
    .eq('read', false);

  return { count: count || 0, error };
}

export async function markNotificationAsRead(notificationId: ID) {
  const { data, error } = await supabase
    .from('task_notifications')
    .update({ read: true })
    .eq('id', notificationId)
    .select()
    .single();

  return { data: data as TaskNotification | null, error };
}

export async function markAllNotificationsAsRead(userId: ID) {
  const { data, error } = await supabase
    .from('task_notifications')
    .update({ read: true })
    .eq('user_id', userId)
    .eq('read', false)
    .select();

  return { data: data as TaskNotification[] | null, error };
}

export async function deleteNotification(notificationId: ID) {
  const { error } = await supabase
    .from('task_notifications')
    .delete()
    .eq('id', notificationId);

  return { error };
}

export async function createTaskNotification(
  taskId: ID,
  userId: ID,
  notificationType: TaskNotification['notification_type'],
  message: string
) {
  const { data, error } = await supabase
    .from('task_notifications')
    .insert({
      task_id: taskId,
      user_id: userId,
      notification_type: notificationType,
      message
    })
    .select(`
      *,
      task:tasks(
        id,
        title,
        project_id,
        project:projects(
          id,
          name,
          company_id,
          company:companies(
            id,
            name
          )
        )
      )
    `)
    .single();

  return { data: data as TaskNotification | null, error };
}